import React, { useCallback, useMemo } from 'react';

import { IconButton } from '@material-ui/core';
import clsx from 'clsx';
import type { ReactNodeLike } from 'prop-types';
import { useTranslation } from 'react-i18next';

import { ReactComponent as MoreIcon } from 'assets/icons/more.svg';
import ContextMenu from 'components/ContextMenu/ContextMenu';
import Table from 'components/Table';
import type { AssetRopaReview } from 'config/api/ropaReviews/ropaReviews.types';
import processing_activities_messages from 'messages/processing_activities_messages';
import type { TableDataMappingRow } from 'types/Table';

import type { AssetRopaTableAvailableFilters, AssetRopaTableFilterState } from '../assetRopaReviewFilters';
import useStyles from '../ProcessingActivitiesAssetReviewsPage.styles';

type ReviewStatusMessageKey = keyof typeof processing_activities_messages.asset_ropa.review_status;
type PermanentRopaStatusMessageKey = keyof typeof processing_activities_messages.asset_ropa.permanent_ropa_status;

type FiltersService = {
  availableFilters: AssetRopaTableAvailableFilters;
  filters: AssetRopaTableFilterState;
  setFilters: React.Dispatch<React.SetStateAction<AssetRopaTableFilterState>>;
};

export type AssetRopaAssetMenuAction =
  | 'open_asset_ropa'
  | 'request_clarification'
  | 'accept_all_submitted'
  | 'cancel_incomplete_asset_review';

type Props = {
  assets: AssetRopaReview[];
  emptyStateContent?: ReactNodeLike;
  expandedAssetId: number | null;
  filtersService?: FiltersService;
  onAssetMenuAction: (asset: AssetRopaReview, action: AssetRopaAssetMenuAction) => void;
  onExpandAsset: (asset: AssetRopaReview) => void;
  renderExpandedAsset: (asset: AssetRopaReview) => React.ReactNode;
  showAdminAssetMenu: boolean;
};

const getReviewStatusDotClass = (status: AssetRopaReview['reviewStatus'], styles: ReturnType<typeof useStyles>) => {
  switch (status) {
    case 'accepted':
      return styles.statusDotSuccess;
    case 'submitted':
      return styles.statusDotInfo;
    case 'clarification_requested':
      return styles.statusDotDanger;
    default:
      return styles.statusDotWarning;
  }
};

const AssetRopaReviewsTable: React.FC<Props> = ({
  assets,
  emptyStateContent,
  expandedAssetId,
  filtersService,
  onAssetMenuAction,
  onExpandAsset,
  renderExpandedAsset,
  showAdminAssetMenu,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const tableMessages = processing_activities_messages.asset_ropa.assets_table;

  const showActionsColumn =
    showAdminAssetMenu &&
    assets.some(
      asset =>
        Boolean(asset.menuActions.accept_all_submitted || asset.menuActions.accept_all_submitted_reviews) ||
        Boolean(asset.menuActions.request_clarification) ||
        Boolean(asset.menuActions.cancel_incomplete_asset_review),
    );

  const dataMapping: TableDataMappingRow<AssetRopaReview>[] = useMemo(() => {
    const columns: TableDataMappingRow<AssetRopaReview>[] = [
      {
        id: 'assetName',
        label: t(tableMessages.asset),
        get: asset => (
          <span className={clsx(styles.assetNameLink, { [styles.assetNameLinkExpanded]: expandedAssetId === asset.id })}>
            {asset.assetName}
          </span>
        ),
      },
      {
        id: 'operationalOwnerName',
        label: t(tableMessages.owner),
        get: asset => asset.operationalOwnerName ?? t(processing_activities_messages.asset_ropa.panel.not_assigned),
      },
      {
        id: 'assetTypeName',
        label: t(tableMessages.asset_type),
        get: asset => asset.assetTypeName,
      },
      {
        id: 'processingActivitiesCount',
        label: t(tableMessages.processing_activities),
        get: asset => asset.processingActivitiesCount,
      },
      {
        id: 'permanentRopaStatus',
        label: t(tableMessages.ropa_status),
        get: asset => {
          const permanentStatusKey = asset.permanentRopaStatus as PermanentRopaStatusMessageKey;
          return processing_activities_messages.asset_ropa.permanent_ropa_status[permanentStatusKey]
            ? t(processing_activities_messages.asset_ropa.permanent_ropa_status[permanentStatusKey])
            : asset.permanentRopaStatus;
        },
      },
      {
        id: 'reviewStatus',
        label: t(tableMessages.review_status),
        get: asset => (
          <span className={styles.statusCell}>
            <span className={clsx(styles.statusDot, getReviewStatusDotClass(asset.reviewStatus, styles))} />
            {t(processing_activities_messages.asset_ropa.review_status[asset.reviewStatus as ReviewStatusMessageKey])}
          </span>
        ),
      },
      {
        id: 'warnings',
        label: t(tableMessages.warnings),
        get: asset => {
          const warningChips = [
            asset.warnings.dpiaIndicated ? t(processing_activities_messages.asset_ropa.warnings.dpia_indicated) : null,
            asset.warnings.dpiaRequiredUnapproved ? t(processing_activities_messages.asset_ropa.warnings.dpia_required_unapproved) : null,
            asset.warnings.thirdCountryProcessing ? t(processing_activities_messages.asset_ropa.warnings.third_country_processing) : null,
            asset.warnings.sensitivePersonalData ? t(processing_activities_messages.asset_ropa.warnings.sensitive_personal_data) : null,
            asset.warnings.confirmedNoProcessingActivities
              ? t(processing_activities_messages.asset_ropa.warnings.confirmed_no_processing_activities)
              : null,
          ].filter(Boolean) as string[];

          if (warningChips.length === 0) {
            return '—';
          }

          return (
            <div className={styles.warningChipsCell}>
              {warningChips.map(chip => (
                <span key={chip} className={styles.warningChip}>
                  {chip}
                </span>
              ))}
            </div>
          );
        },
      },
    ];

    if (showActionsColumn) {
      columns.push({
        id: 'actions',
        blockSorting: true,
        isAddon: true,
        label: '',
        get: asset => {
          const menuItems = [
            ...(asset.menuActions.accept_all_submitted || asset.menuActions.accept_all_submitted_reviews
              ? [
                  {
                    label: t(processing_activities_messages.asset_ropa.actions.accept_all_submitted),
                    onClick: () => onAssetMenuAction(asset, 'accept_all_submitted'),
                  },
                ]
              : []),
            ...(asset.menuActions.request_clarification
              ? [
                  {
                    label: t(processing_activities_messages.asset_ropa.actions.request_clarification),
                    onClick: () => onAssetMenuAction(asset, 'request_clarification'),
                  },
                ]
              : []),
            ...(asset.menuActions.cancel_incomplete_asset_review
              ? [
                  {
                    label: t(processing_activities_messages.asset_ropa.actions.cancel_incomplete_asset_review),
                    onClick: () => onAssetMenuAction(asset, 'cancel_incomplete_asset_review'),
                  },
                ]
              : []),
          ];

          if (menuItems.length === 0) {
            return null;
          }

          return (
            <div className={styles.rowActions}>
              <ContextMenu elements={menuItems}>
                <IconButton aria-label={t(processing_activities_messages.asset_ropa.actions.asset_menu)} size='small'>
                  <MoreIcon />
                </IconButton>
              </ContextMenu>
            </div>
          );
        },
      });
    }

    return columns;
  }, [expandedAssetId, onAssetMenuAction, showActionsColumn, styles, t, tableMessages]);

  const isRowExpanded = useCallback((asset: AssetRopaReview) => expandedAssetId === asset.id, [expandedAssetId]);

  return (
    <div
      aria-label={t(tableMessages.aria_label)}
      className={clsx(styles.tableScrollWrapper, { [styles.tableScrollWrapperWithActions]: showActionsColumn })}
      role='region'
    >
      <Table
        clickableRow
        data={assets}
        dataMapping={dataMapping}
        emptyStateContent={emptyStateContent}
        filterInLastColumn={Boolean(filtersService)}
        filtersService={filtersService}
        isRowExpanded={isRowExpanded}
        isRowSelected={isRowExpanded}
        onRowClick={(_, asset) => onExpandAsset(asset)}
        refetching={false}
        renderExpandedRow={renderExpandedAsset}
        rowAriaLabel={asset => t(tableMessages.row_aria_label, { name: asset.assetName })}
      />
    </div>
  );
};

export default AssetRopaReviewsTable;
